import { Injectable, OnDestroy, inject, signal } from '@angular/core';
import {
  CollectionReference,
  Firestore,
  collection,
  collectionData,
  getDocs,
  query,
  where,
} from '@angular/fire/firestore';
import { Subject, filter, from, switchMap, takeUntil } from 'rxjs';
import mapboxgl from 'mapbox-gl';
import { MapService } from './map.service';
import { CustomLayerWithZoom, Discovery, IndexedDiscovery, RenderedDiscovery } from '../../interfaces/master-interfaces';
import { buildIconMarkup, ICON_REGISTRY } from '../../interfaces/discoveries-icons';
import * as THREE from 'three';



/** Pad bounds so cells whose CENTER sits just off-screen are still fetched. */
const BUCKET_PAD_DEG = 0.01;   // ~1.1km — larger than a precision-6 cell diagonal
const LAYER_ID = 'discovery-billboards';
const HEATMAP_SOURCE = 'discovery-density';
const HEATMAP_LAYER = 'discovery-heatmap';
const BBOX_SOURCE = 'discovery-bbox';
const BBOX_LAYER = 'discovery-bbox-outline';
const REGION_SOURCE = 'region-cells';
const REGION_FILL_LAYER = 'region-fill';
const REGION_OUTLINE_LAYER = 'region-outline';
const REGION_LABEL_LAYER = 'region-label';
const REGION_PRECISION = 4;
const REGION_DISCOVERY_TARGET = 25;
const BUCKET_PRECISION = 6;

/** Zoom at or above which meshes render. Below this, heatmap only. */
const MESH_MINZOOM = 8;

/** Physical height of a landmark billboard, metres. Tune to taste. */
const LANDMARK_M = 120;

/** Screen-size floor so distant landmarks stay clickable, pixels. */
const MIN_PIXEL_HEIGHT = 10;

/** Texture rasterization size. */
const ICON_PX = 256;

/** Click tolerance, screen pixels. */
const CLICK_RADIUS_PX = 28;

const EMPTY_FC: GeoJSON.FeatureCollection = {
  type: 'FeatureCollection',
  features: [],
};



/**
 * Owns every Mapbox side effect for discoveries: the density heatmap,
 * the per-POI markers, and the detail popup.
 *
 * Provide at COMPONENT level, not root:
 *   @Component({ providers: [DiscoveryRendererService] })
 * so ngOnDestroy fires when the map component is torn down.
 */
@Injectable({ providedIn: 'root' })



export class DiscoveryRendererService implements OnDestroy {



  private readonly _firestore = inject(Firestore);
  private readonly _mapService = inject(MapService);
  private readonly _destroy$ = new Subject<void>();
  private readonly _selected$ = new Subject<IndexedDiscovery>();
  private static readonly GEOHASH_BASE32 = '0123456789bcdefghjkmnpqrstuvwxyz';
  readonly discoverySelected$ = this._selected$.asObservable();

  /** For the HUD counters. */
  readonly visibleCount = signal(0);
  readonly discoveries = signal<IndexedDiscovery[]>([]);
  private map: mapboxgl.Map | null = null;
  private renderer: THREE.WebGLRenderer | null = null;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.Camera();
  private readonly rendered = new Map<string, RenderedDiscovery>();
  private readonly textures = new Map<string, THREE.Texture>();
  private readonly materials = new Map<string, THREE.MeshBasicMaterial>();
  /** Unit plane, origin translated to the BOTTOM edge so it stands on ground. */
  private readonly geometry = (() => {
    const g = new THREE.PlaneGeometry(1, 1);
    g.translate(0, 0.5, 0);
    return g;
  })();

  private currentPopup: mapboxgl.Popup | null = null;
  private currentZoomLevel: number | null = null;
  private layersReady: Promise<void> | null = null;
  private lastZoom = 0;

  private moveEndHandler: (() => void) | null = null;
  private clickHandler: ((e: mapboxgl.MapMouseEvent) => void) | null = null;

  // ---------------------------------------------------------------- lifecycle

  start(): void {
    this._mapService.mapInitialized
      .pipe(
        filter((initialized) => initialized),
        switchMap(() => from(this.ensureLayers())),
        switchMap(() => {
          const densityRef = collection(
            this._firestore,
            'discoveries-geohash-5'
          ) as CollectionReference<any>;

          return collectionData<any>(densityRef, { idField: 'geohash' });
        }),
        takeUntil(this._destroy$)
      )
      .subscribe({
        next: (densityBuckets: any[]) => {
          this.updateHeatmapData(densityBuckets);
          this.bindMapListeners();
        },
        error: (error) => console.error('❌ Error in discovery listener:', error),
      });

    this._mapService.mapInitialized
      .pipe(
        filter((initialized) => initialized),
        switchMap(() => from(this.ensureLayers())),
        switchMap(() => {
          const regionsRef = collection(this._firestore, 'regions') as CollectionReference<any>;
          return collectionData<any>(regionsRef, { idField: 'geohash4' });
        }),
        takeUntil(this._destroy$)
      )
      .subscribe({
        next: (regions: any[]) => this.updateRegionData(regions),
        error: (error) => console.error('❌ Error in region listener:', error),
      });
  }



  // ------------------------------------------------------------------ layers

  private ensureLayers(): Promise<void> {
    if (this.layersReady) return this.layersReady;

    const map = this._mapService.getMapIfReady();
    if (!map) return Promise.resolve();

    this.map = map;

    this.layersReady = this.registerTextures()
      .then(() => {
        // console.log(`🎨 Textures ready: ${this.textures.size}/${Object.keys(ICON_REGISTRY).length}`);
        this.addRegionLayer(map);
        this.addHeatmapLayer(map);
        this.addBboxLayer(map);
        this.addCustomLayer(map);
      })
      .catch((error) => {
        console.error('❌ Failed to build discovery layers:', error);
        this.layersReady = null;
        throw error;
      });

    return this.layersReady;
  }

  private addCustomLayer(map: mapboxgl.Map): void {
    if (map.getLayer(LAYER_ID)) return;

    const layer: CustomLayerWithZoom = {
      id: LAYER_ID,
      type: 'custom',
      renderingMode: '3d',
      minzoom: MESH_MINZOOM,
      onAdd: (_m, gl) => {
        // Shares the map's GL context, exactly like BeingRendererService.
        this.renderer = new THREE.WebGLRenderer({
          context: gl,
          canvas: gl.canvas,
          antialias: true,
          alpha: true,
        });
        this.renderer.autoClear = false;
        this.renderer.setPixelRatio(window.devicePixelRatio);
      },
      render: (_gl, matrix) => this.renderFrame(matrix as number[]),
      onRemove: () => {
        // Do NOT dispose the renderer's GL context — Mapbox owns it.
        this.renderer = null;
      },
    };

    map.addLayer(layer as mapboxgl.CustomLayerInterface);
  }

  private addHeatmapLayer(map: mapboxgl.Map): void {
    if (!map.getSource(HEATMAP_SOURCE)) {
      map.addSource(HEATMAP_SOURCE, { type: 'geojson', data: EMPTY_FC });
    }
    if (map.getLayer(HEATMAP_LAYER)) return;

    map.addLayer({
      id: HEATMAP_LAYER,
      type: 'heatmap',
      source: HEATMAP_SOURCE,
      maxzoom: 24,
      paint: {
        'heatmap-weight': ['interpolate', ['linear'], ['get', 'mag'], 0, 0, 1, 1],
        'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 0, 1, 7, 3],
        'heatmap-color': [
          'interpolate',
          ['linear'],
          ['heatmap-density'],
          0, 'rgba(30, 64, 175, 0)',
          0.3, '#1e40af',
          0.5, '#3b82f6',
          0.7, '#fbbf24',
          0.85, '#f97316',
          1, '#dc2626',
        ],
        'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 0, 8, 7, 15],
        'heatmap-opacity': 1,
      },
    });
  }

  private addBboxLayer(map: mapboxgl.Map): void {
    if (!map.getSource(BBOX_SOURCE)) {
      map.addSource(BBOX_SOURCE, { type: 'geojson', data: EMPTY_FC });
    }
    if (map.getLayer(BBOX_LAYER)) return;

    map.addLayer({
      id: BBOX_LAYER,
      type: 'line',
      source: BBOX_SOURCE,
      paint: {
        'line-color': '#3b82f6',
        'line-width': 2,
        'line-opacity': 0.7,
        'line-dasharray': [4, 2],
      },
    });
  }


  private removeLayers(): void {
    const map = this.map;
    if (!map) return;

    if (map.getLayer(LAYER_ID)) map.removeLayer(LAYER_ID);
    if (map.getLayer(HEATMAP_LAYER)) map.removeLayer(HEATMAP_LAYER);
    if (map.getSource(HEATMAP_SOURCE)) {
      this._mapService.safeRemoveSource(HEATMAP_SOURCE);
    }

    if (map.getLayer(BBOX_LAYER)) map.removeLayer(BBOX_LAYER);
    if (map.getSource(BBOX_SOURCE)) {
      this._mapService.safeRemoveSource(BBOX_SOURCE);
    }

    [REGION_LABEL_LAYER, REGION_OUTLINE_LAYER, REGION_FILL_LAYER].forEach((id) => {
      if (map.getLayer(id)) map.removeLayer(id);
    });
    if (map.getSource(REGION_SOURCE)) {
      this._mapService.safeRemoveSource(REGION_SOURCE);
    }

    this.layersReady = null;
  }



  // ---------------------------------------------------------------- textures

  private async registerTextures(): Promise<void> {
    const keys = Object.keys(ICON_REGISTRY);
    //  console.log('🎨 Starting texture registration for:', keys);

    return Promise.all(keys.map((k) => this.loadTexture(k)))
      .then(() => {
        // console.log('🎨 Texture registration complete. Loaded:', Array.from(this.textures.keys()));
        // console.log(`🎨 Textures: ${this.textures.size}/${keys.length}`);
      });
  }

  private loadTexture(key: string): Promise<void> {
    if (this.textures.has(key)) return Promise.resolve();

    return new Promise<void>((resolve) => {
      const svg = buildIconMarkup(key, ICON_PX);
      if (!svg) {
        console.warn(`⚠️ No markup for icon: ${key}`);
        return resolve();
      }

      const img = new Image(ICON_PX, ICON_PX);

      img.onload = () => {
        const texture = new THREE.Texture(img);
        texture.needsUpdate = true;
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.minFilter = THREE.LinearMipmapLinearFilter;
        texture.magFilter = THREE.LinearFilter;
        texture.generateMipmaps = false;
        texture.anisotropy = 4;
        this.textures.set(key, texture);
        // console.log(`✅ ${key} texture loaded, applied to textures map (size=${this.textures.size})`);
        resolve();
      };
      img.onerror = () => {
        console.error(`❌ Icon texture load failed: ${key}`);
        resolve();
      };

      img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
    });
  }

  private materialFor(category: string): THREE.MeshBasicMaterial | null {
    const cached = this.materials.get(category);
    if (cached) {
      // console.log(`📦 materialFor(${category}): using cached material`);
      return cached;
    }

    const texture = this.textures.get(category);
    // console.log(`🔍 materialFor(${category}): looking for texture... found=${!!texture}, available textures:`, Array.from(this.textures.keys()));

    if (!texture) return null;

    const material = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      alphaTest: 0.02,
      depthWrite: false,
      depthTest: false,
      side: THREE.DoubleSide,
    });
    this.materials.set(category, material);
    console.log(`✅ Created material for ${category}`);
    return material;
  }


  // ------------------------------------------------------------------ meshes

  private setDiscoveries(discoveries: IndexedDiscovery[]): void {
    const incoming = new Set<string>();
    const bboxFeatures: GeoJSON.Feature<GeoJSON.Polygon>[] = [];

    for (const d of discoveries) {
      if (!d.location) continue;
      incoming.add(d.id);

      const existing = this.rendered.get(d.id);
      if (existing) {
        existing.lng = d.location.lng;
        existing.lat = d.location.lat;
        existing.discovery = d;
      } else {
        this.addDiscoveryMesh(d);
      }

      // Build bbox feature
      const bbox = d.nominatimData?.boundingbox;
      if (bbox && bbox.length === 4) {
        const [swLat, neLat, swLng, neLng] = bbox.map(Number);
        bboxFeatures.push({
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [[[swLng, swLat], [neLng, swLat], [neLng, neLat], [swLng, neLat], [swLng, swLat]]],
          },
          properties: { id: d.id },
        });
      }
    }

    const source = this.map?.getSource(BBOX_SOURCE) as mapboxgl.GeoJSONSource | undefined;
    if (source) {
      source.setData({ type: 'FeatureCollection', features: bboxFeatures });
    }

    for (const [id, r] of this.rendered) {
      if (incoming.has(id)) continue;

      // Only cull if actually outside the viewport — not just absent from this query
      const bounds = this.map?.getBounds();
      if (bounds && bounds.contains([r.lng, r.lat])) continue;

      this.scene.remove(r.mesh);
      // (r.mesh.material as THREE.Material).dispose();
      this.rendered.delete(id);
    }

    this.visibleCount.set(this.rendered.size);
    this.discoveries.set(discoveries);
    this.map?.triggerRepaint();

    // console.log(
    //   `🔷 rendered=${this.rendered.size} zoom=${this.map?.getZoom().toFixed(1)} ` +
    //   `vis=${this.map?.getLayoutProperty(LAYER_ID, 'visibility')} ` +
    //   `scene=${this.scene.children.length}`
    // );
  }


  private addDiscoveryMesh(d: IndexedDiscovery): void {
    // Resolve the icon key (prefer poiTypes, fall back to category)
    const iconKey = this.resolveIconKey(d);

    // console.log(`Adding mesh for ${d.id}: poiTypes=${d.poiTypes?.[0]}, category=${d.category}, resolved=${iconKey}`);

    const material = this.materialFor(iconKey);
    if (!material) {
      console.warn(`⚠️ No material for ${iconKey}`);
      return;
    }

    const mesh = new THREE.Mesh(this.geometry, material);
    mesh.frustumCulled = false;
    this.scene.add(mesh);

    this.rendered.set(d.id, {
      mesh,
      lng: d.location.lng,
      lat: d.location.lat,
      discovery: d,
    });
  }

  private resolveIconKey(d: Discovery): string {
    // Prefer poiTypes[0] over category
    if (d.poiTypes && d.poiTypes.length > 0 && ICON_REGISTRY[d.poiTypes[0]]) {
      return d.poiTypes[0];
    }

    if (ICON_REGISTRY[d.category]) {
      return d.category;
    }

    return 'LANDMARK';  // fallback
  }

  private clearDiscoveries(): void {
    this.rendered.forEach((r) => {
      this.scene.remove(r.mesh);
      //(r.mesh.material as THREE.Material).dispose();
    });
    this.rendered.clear();
    this.visibleCount.set(0);
  }

  // ------------------------------------------------------------------- frame

  private renderFrame(matrix: number[]): void {
    if (!this.map || !this.renderer || this.rendered.size === 0) return;

    const zoom = this.map.getZoom();
    if (zoom < MESH_MINZOOM) return;   // custom layers ignore minzoom

    const centre = this.map.getCenter();
    const anchor = mapboxgl.MercatorCoordinate.fromLngLat(centre, 0);
    const metre = anchor.meterInMercatorCoordinateUnits();

    const latRad = (centre.lat * Math.PI) / 180;
    const metresPerPixel =
      (40075016.686 * Math.cos(latRad)) / (512 * Math.pow(2, zoom));

    // Physical size, with a screen-space floor so distant icons stay visible.
    if (!isFinite(metresPerPixel) || metresPerPixel < 0) {
      console.warn('Bad metresPerPixel:', metresPerPixel);
      return;
    }
    const heightM = Math.max(LANDMARK_M, MIN_PIXEL_HEIGHT * metresPerPixel);

    // Billboard orientation: tilt to match pitch, yaw to face the camera.
    const pitchRad = (this.map.getPitch() * Math.PI) / 180;
    const bearingRad = (this.map.getBearing() * Math.PI) / 180;

    const qTilt = new THREE.Quaternion().setFromAxisAngle(
      new THREE.Vector3(1, 0, 0),
      pitchRad
    );
    const qYaw = new THREE.Quaternion().setFromAxisAngle(
      new THREE.Vector3(0, 0, 1),
      -bearingRad
    );
    const orientation = qTilt.multiply(qYaw);

    for (const r of this.rendered.values()) {
      const ground = this.groundElevation(r.lng, r.lat);

      const merc = mapboxgl.MercatorCoordinate.fromLngLat(
        { lng: r.lng, lat: r.lat },
        ground
      );

      r.mesh.position.set(
        (merc.x - anchor.x) / metre,
        -(merc.y - anchor.y) / metre,
        (merc.z - anchor.z) / metre
      );
      r.mesh.quaternion.copy(orientation);
      r.mesh.scale.setScalar(heightM);
    }

    const anchorTransform = new THREE.Matrix4()
      .makeTranslation(anchor.x, anchor.y, anchor.z)
      .scale(new THREE.Vector3(metre, -metre, metre));

    this.camera.projectionMatrix = new THREE.Matrix4()
      .fromArray(matrix)
      .multiply(anchorTransform);

    this.renderer.resetState();
    this.renderer.getContext().depthRange(0, 1);   // ← Mapbox narrowed this; Three won't restore it
    this.renderer.render(this.scene, this.camera);
  }

  private groundElevation(lng: number, lat: number): number {
    const elev = this.map?.queryTerrainElevation({ lng, lat }) ?? 0;
    //  if (this.rendered.size && Math.random() < 0.01) console.log('⛰️ elev', elev);
    if (elev === undefined || isNaN(elev)) return 0;
    return elev;
  }

  // ------------------------------------------------------------------ events

  private bindMapListeners(): void {
    if (this.moveEndHandler) return;

    const map = this._mapService.getMapIfReady();
    if (!map) return;

    this.lastZoom = map.getZoom();

    this.moveEndHandler = () => {
      const currentZoom = map.getZoom();
      const zoomChanged = Math.abs(currentZoom - this.lastZoom) >= 0.3;
      this.lastZoom = currentZoom;

      if (zoomChanged) {
        this.applyZoomMode(currentZoom);
      } else if (currentZoom >= MESH_MINZOOM) {
        this.loadDiscoveriesByZoom(BUCKET_PRECISION);
      }
    };
    map.on('moveend', this.moveEndHandler);

    // Meshes aren't queryable features, so hit-test in screen space.
    this.clickHandler = (e: mapboxgl.MapMouseEvent) => this.pickAt(e.point);
    map.on('click', this.clickHandler);

    this.applyZoomMode(this.lastZoom);
  }

  private unbindMapListeners(): void {
    const map = this.map;
    if (map) {
      if (this.moveEndHandler) map.off('moveend', this.moveEndHandler);
      if (this.clickHandler) map.off('click', this.clickHandler);
    }
    this.moveEndHandler = null;
    this.clickHandler = null;
  }

  /** Nearest discovery within CLICK_RADIUS_PX of the tap, or nothing. */
  private pickAt(point: mapboxgl.Point): void {
    const map = this.map;
    if (!map || map.getZoom() < MESH_MINZOOM) return;

    let best: RenderedDiscovery | null = null;
    let bestDist = CLICK_RADIUS_PX;

    for (const r of this.rendered.values()) {
      const screen = map.project([r.lng, r.lat]);
      const dx = screen.x - point.x;
      const dy = screen.y - point.y;
      const dist = Math.hypot(dx, dy);

      // Bias upward: the icon body sits above its ground anchor.
      if (dy < 0 && dy > -60) {
        if (dist < bestDist + 30) {
          bestDist = Math.min(dist, bestDist);
          best = r;
        }
      } else if (dist < bestDist) {
        bestDist = dist;
        best = r;
      }
    }

    if (best) this.selectDiscovery(best.discovery);
  }

  private applyZoomMode(zoom: number): void {
    if (zoom < MESH_MINZOOM) {
      this.setLayerVisible(HEATMAP_LAYER, true);
      this.setLayerVisible(LAYER_ID, false);
      this.clearDiscoveries();
      this.map?.triggerRepaint();
    } else {
      this.setLayerVisible(HEATMAP_LAYER, false);
      this.setLayerVisible(LAYER_ID, true);
      this.loadDiscoveriesByZoom(BUCKET_PRECISION);
    }
  }

  private setLayerVisible(layerId: string, visible: boolean): void {
    const map = this.map;
    if (!map || !map.getLayer(layerId)) return;

    map.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none');

  }

  // ------------------------------------------------------------------- data

  private updateHeatmapData(buckets: any[]): void {
    const source = this.map?.getSource(HEATMAP_SOURCE) as
      | mapboxgl.GeoJSONSource
      | undefined;
    if (!source) return;

    source.setData({
      type: 'FeatureCollection',
      features: buckets.map((bucket) => ({
        type: 'Feature' as const,
        geometry: {
          type: 'Point' as const,
          coordinates: [bucket.centerLng, bucket.centerLat],
        },
        properties: { count: bucket.count, mag: bucket.count / 100 },
      })),
    });
  }

  loadDiscoveriesByZoom(geohashLevel: number): void {
    const map = this.map;
    if (!map) return;

    const bounds = map.getBounds();
    if (!bounds) return;

    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();

    this.currentZoomLevel = geohashLevel;
    this.loadAggregationBuckets(
      geohashLevel,
      sw.lat - BUCKET_PAD_DEG,
      sw.lng - BUCKET_PAD_DEG,
      ne.lat + BUCKET_PAD_DEG,
      ne.lng + BUCKET_PAD_DEG
    );
  }

  private loadAggregationBuckets(
    level: number,
    swLat: number,
    swLng: number,
    neLat: number,
    neLng: number
  ): void {
    const requestLevel = level;

    getDocs(
      query(
        collection(this._firestore, `discoveries-geohash-${level}`),
        where('centerLat', '>=', swLat),
        where('centerLat', '<=', neLat),
        where('centerLng', '>=', swLng),
        where('centerLng', '<=', neLng)
      )
    )
      .then((snap) => {
        if (this.currentZoomLevel !== requestLevel) return [];
        const geohashes = snap.docs.map((d) => d.data()['geohash']);
        //  console.log(`🪣 geohash-${level}: ${snap.docs.length} buckets → querying`);
        return this.loadDiscoveriesByGeohashes(level, geohashes);
      })
      .then((discoveries) => {
        if (this.currentZoomLevel !== requestLevel) return;
        if (discoveries.length === 0 && this.rendered.size > 0) {
          console.warn(`⚠️ geohash-${level}: empty result, keeping ${this.rendered.size} meshes`);
          return;
        }
        this.setDiscoveries(discoveries);
      })
      .catch((error) => {
        console.error(`❌ Error loading geohash-${level}:`, error);
      });
  }

  private async loadDiscoveriesByGeohashes(
    level: number,
    geohashes: string[]
  ): Promise<IndexedDiscovery[]> {
    if (geohashes.length === 0) return [];

    const chunks = this.chunkArray(geohashes, 10);
    const snapshots = await Promise.all(
      chunks.map((chunk) =>
        getDocs(
          query(
            collection(this._firestore, 'discoveries-full'),
            where(`geohash${level}`, 'in', chunk)
          )
        )
      )
    );

    const discoveries: IndexedDiscovery[] = [];
    snapshots.forEach((snap) => {
      snap.docs.forEach((doc) => {
        discoveries.push({ ...(doc.data() as Discovery), id: doc.id });
      });
    });

    return discoveries;
  }

  /** Firestore 'in' takes at most 10 values per query. */
  private chunkArray<T>(arr: T[], size: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < arr.length; i += size) {
      chunks.push(arr.slice(i, i + size));
    }
    return chunks;
  }

  // ------------------------------------------------------------------ popup

  private selectDiscovery(discovery: IndexedDiscovery): void {
    const map = this.map;
    if (!map || !discovery.location) return;

    this.currentPopup?.remove();

    const { lat, lng } = discovery.location;
    const nominatimName = this.escapeHtml(
      discovery.nominatimData?.displayName || 'Unknown Location'
    );

    // Show the resolved icon type, not just category
    const iconKey = this.resolveIconKey(discovery);
    const displayType = discovery.poiTypes?.[0] || discovery.category;

    const notes = discovery.notes ? this.escapeHtml(discovery.notes) : '';
    const poiList = discovery.poiTypes?.slice(0, 5).join(', ') || '';

    const popupHTML = `
      <div style="font-family: system-ui, -apple-system, sans-serif; padding: 12px; max-width: 300px;">
        <p style="margin: 4px 0; font-size: 12px; color: #666;">
          <strong>${nominatimName}</strong>
        </p>
        <p style="margin: 4px 0; font-size: 11px; color: #999;">
          <strong>Type:</strong> ${displayType} | <strong>Category:</strong> ${discovery.category}
        </p>
        ${poiList ? `<p style="margin: 4px 0; font-size: 10px; color: #aaa;">📍 ${poiList}</p>` : ''}
        ${notes ? `<p style="margin: 8px 0 0 0; font-size: 11px; color: #999; font-style: italic;">${notes}</p>` : ''}
      </div>
    `;

    this.currentPopup = new mapboxgl.Popup({
      closeButton: true,
      closeOnClick: true,
      maxWidth: '400px',
      anchor: 'bottom',
      offset: 24,
    })
      .setLngLat([lng, lat])
      .setHTML(popupHTML)
      .addTo(map);

    this._selected$.next(discovery);
  }


  /**
   * beingName / notes / display_name are being-generated or UGC-derived.
   * Mapbox setHTML does not sanitize, so escape before interpolating.
   */
  private escapeHtml(value: string): string {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
  }

  private addRegionLayer(map: mapboxgl.Map): void {
    if (!map.getSource(REGION_SOURCE)) {
      map.addSource(REGION_SOURCE, { type: 'geojson', data: EMPTY_FC });
    }
    if (map.getLayer(REGION_FILL_LAYER)) return;

    map.addLayer({
      id: REGION_FILL_LAYER,
      type: 'fill',
      source: REGION_SOURCE,
      paint: {
        // progress 0 → cool and faint, 1 → green and solid
        'fill-color': [
          'interpolate', ['linear'], ['get', 'progress'],
          0, '#1e40af',
          0.6, '#fbbf24',
          1, '#22c55e',
        ],
        'fill-opacity': [
          'interpolate', ['linear'], ['get', 'progress'],
          0, 0.06,
          1, 0.28,
        ],
      },
    });

    map.addLayer({
      id: REGION_OUTLINE_LAYER,
      type: 'line',
      source: REGION_SOURCE,
      paint: {
        'line-color': [
          'match', ['get', 'status'],
          'cradle', '#22c55e',
          'open', '#60a5fa',
          '#64748b',
        ],
        'line-dasharray': [3, 2],
        'line-width': 2,
        'line-opacity': 0.9,
      },
    });

    // map.addLayer({
    //   id: REGION_LABEL_LAYER,
    //   type: 'symbol',
    //   source: REGION_SOURCE,
    //   layout: {
    //     'text-field': [
    //       'concat', ['get', 'geohash'], '  ',
    //       ['to-string', ['get', 'livingBeings']], '/', ['to-string', ['get', 'populationTarget']],
    //     ],
    //     'text-size': 12,
    //     'text-anchor': 'top',
    //     'text-offset': [0, 0.4],
    //   },
    //   paint: {
    //     'text-color': '#e2e8f0',
    //     'text-halo-color': '#0f172a',
    //     'text-halo-width': 1.5,
    //   },
    // });

    // map.setLayoutProperty(REGION_FILL_LAYER, 'visibility', 'none');
    // map.setLayoutProperty(REGION_OUTLINE_LAYER, 'visibility', 'none');
  }


  private updateRegionData(regions: any[]): void {
    const source = this.map?.getSource(REGION_SOURCE) as mapboxgl.GeoJSONSource | undefined;
    if (!source) return;

    const features: GeoJSON.Feature<GeoJSON.Polygon>[] = [];

    for (const region of regions) {
      const gh = region?.geohash4;
      if (typeof gh !== 'string' || gh.length !== REGION_PRECISION) continue;

      // 🆕 hide regions that have no discoveries left
      const count = region.discoveryCount ?? 0;
      if (count === 0) continue;

      const b = this.decodeGeohashBounds(gh);
      if (!b) continue;

      const progress = Math.min(count / REGION_DISCOVERY_TARGET, 1);

      features.push({
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [[
            [b.lngMin, b.latMin],
            [b.lngMax, b.latMin],
            [b.lngMax, b.latMax],
            [b.lngMin, b.latMax],
            [b.lngMin, b.latMin],
          ]],
        },
        properties: {
          geohash: gh,
          count,
          progress,
          status: region.status ?? 'unknown',
          habitable: !!region.habitable,
          livingBeings: region.livingBeings ?? 0,
          populationTarget: region.populationTarget ?? 0,
        },
      });
    }

    source.setData({ type: 'FeatureCollection', features });
  }



  private decodeGeohashBounds(hash: string): { latMin: number; latMax: number; lngMin: number; lngMax: number } | null {
    let evenBit = true;
    let latMin = -90, latMax = 90, lngMin = -180, lngMax = 180;

    for (const ch of hash.toLowerCase()) {
      const idx = DiscoveryRendererService.GEOHASH_BASE32.indexOf(ch);
      if (idx < 0) return null;

      for (let n = 4; n >= 0; n--) {
        const bit = (idx >> n) & 1;
        if (evenBit) {
          const mid = (lngMin + lngMax) / 2;
          if (bit === 1) lngMin = mid; else lngMax = mid;
        } else {
          const mid = (latMin + latMax) / 2;
          if (bit === 1) latMin = mid; else latMax = mid;
        }
        evenBit = !evenBit;
      }
    }
    return { latMin, latMax, lngMin, lngMax };
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
    this._selected$.complete();

    this.unbindMapListeners();
    this.clearDiscoveries();
    this.removeLayers();

    this.geometry.dispose();
    this.materials.forEach((m) => m.dispose());
    this.materials.clear();
    this.textures.forEach((t) => t.dispose());
    this.textures.clear();

    this.currentPopup?.remove();
    this.currentPopup = null;
    this.map = null;
  }
}